import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from 'src/app/core/models/api-response';
import { getDepartmentId, getTenantId } from 'src/app/services/store-snapshot.service';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { environment } from 'src/environments/environment';
import { IdName } from '../../shared/models/id-name';
import {
  IRunTimeRequest,
  IRunTime,
  IRunTimeCounterUpdateRequest,
  IRunTimeRecordLogs,
  IRunTimeMinimal,
} from '../models/run-time.model';

const headers = { 'content-type': 'application/json' };

@Injectable({
  providedIn: 'root',
})
export class RunTimeApiService {
  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/Runtimes`;
  }
  private headers = { 'content-type': 'application/json' };

  constructor(private http: HttpClient) {}

  loadRunTimesForDepartment(
    currentFilter?: MainTableFilterModel
  ): Observable<ApiResponse<IRunTime[]>> {
    const httpOptions = {
      headers: this.headers,
      params: new HttpParams(),
    };
    const url = `${this.url}/Department`;

    if (currentFilter) {
      httpOptions.params = httpOptions.params.append('Page', currentFilter.pageIndex);
      httpOptions.params = httpOptions.params.append('PageSize', currentFilter.pageSize);
    }
    if (currentFilter?.searchTerm) {
      httpOptions.params = httpOptions.params.append('GlobalSearch', currentFilter.searchTerm);
      httpOptions.params = httpOptions.params.append('IncludeChildren', true);
    }
    if (currentFilter?.orderBy && currentFilter.ascDesc) {
      httpOptions.params = httpOptions.params.append(
        `OrderBy[${currentFilter.orderBy}]`,
        currentFilter.ascDesc
      );
    }
    return this.http.get<ApiResponse<IRunTime[]>>(url, httpOptions);
  }

  loadRunTimeChildrenForTable(
    id: string,
    currentFilter?: MainTableFilterModel,
    orderBy?: string,
    ascDesc?: string
  ): Observable<ApiResponse<IRunTime[]>> {
    const httpOptions = {
      headers: this.headers,
      params: new HttpParams(),
    };
    const url = `${this.url}/${id}/Children`;

    if (currentFilter) {
      httpOptions.params = httpOptions.params.append('Page', currentFilter.pageIndex);
      httpOptions.params = httpOptions.params.append('PageSize', currentFilter.pageSize);
    }
    if (currentFilter?.searchTerm) {
      httpOptions.params = httpOptions.params.append('GlobalSearch', currentFilter.searchTerm);
    }
    if (orderBy && ascDesc) {
      httpOptions.params = httpOptions.params.append(`OrderBy[${orderBy}]`, ascDesc);
    }
    return this.http.get<ApiResponse<IRunTime[]>>(url, httpOptions);
  }

  loadRunTimesForDepartmentMinimal(
    currentFilter?: MainTableFilterModel
  ): Observable<ApiResponse<IRunTimeMinimal[]>> {
    const httpOptions = {
      headers: this.headers,
      params: new HttpParams(),
    };
    const url = `${this.url}/Department/List/v2`;

    if (currentFilter) {
      httpOptions.params = httpOptions.params.append('Page', currentFilter.pageIndex);
      httpOptions.params = httpOptions.params.append('PageSize', currentFilter.pageSize);
    }
    if (currentFilter?.searchTerm) {
      httpOptions.params = httpOptions.params.append('GlobalSearch', currentFilter.searchTerm);
    }
    if (currentFilter?.orderBy && currentFilter.ascDesc) {
      httpOptions.params = httpOptions.params.append(
        `OrderBy[${currentFilter.orderBy}]`,
        currentFilter.ascDesc
      );
    }
    return this.http.get<ApiResponse<IRunTimeMinimal[]>>(url, httpOptions);
  }

  loadAllRunTimesForDepartment(includeChildren?: boolean): Observable<IdName[]> {
    const url = `${this.url}/Department/List`;
    if (includeChildren !== undefined) {
      return this.http.request<IdName[]>('GET', url, { body: { includeChildren } });
    }
    return this.http.get<IdName[]>(url);
  }

  loadRunTimeById(id: string): Observable<IRunTime> {
    const url = `${this.url}/${id}`;
    return this.http.get<IRunTime>(url);
  }

  createRunTime(runTime: IRunTimeRequest): Observable<IRunTime> {
    return this.http.post<IRunTime>(this.url, runTime, { headers });
  }

  updateRunTime(runTime: IRunTimeRequest): Observable<IRunTime> {
    const url = `${this.url}/${runTime.id}`;
    return this.http.put<IRunTime>(url, runTime, { headers });
  }

  changeRunTimeParent(id: string, parentRuntimeId?: string): Observable<void> {
    const url = `${this.url}/${id}/ChangeParent`;
    const body = {
      parentRuntimeId,
    };
    return this.http.patch<void>(url, body, { headers });
  }

  updateRunTimeCounter(runTime: IRunTimeCounterUpdateRequest): Observable<IRunTime> {
    const url = `${this.url}/${runTime.id}/Counter`;
    return this.http.put<IRunTime>(url, runTime, { headers });
  }

  deleteRunTime(id: string): Observable<void> {
    const url = `${this.url}/${id}`;
    return this.http.delete<void>(url);
  }

  changeOffset(runTime: IRunTimeCounterUpdateRequest): Observable<string> {
    const url = `${this.url}/${runTime.id}/Offset`;
    return this.http.put<string>(url, runTime);
  }

  loadLog(id: string): Observable<IRunTimeRecordLogs> {
    const url = `${this.url}/${id}/Log`;
    return this.http.get<IRunTimeRecordLogs>(url);
  }

  printLog(id: string): Observable<Blob> {
    const url = `${this.url}/${id}/Log/Report`;
    return this.http.get(url, { responseType: 'blob' });
  }

  printReport(
    filter?: MainTableFilterModel,
    menuId?: string,
    includeChildren?: boolean
  ): Observable<Blob> {
    const url = `${this.url}/Report`;
    let params = new HttpParams();
    if (filter?.searchTerm) {
      params = params.append('GlobalSearch', filter.searchTerm);
    }
    if (filter?.orderBy && filter.ascDesc) {
      params = params.append(`OrderBy[${filter.orderBy}]`, filter.ascDesc);
    }
    if (menuId) {
      params = params.append('MenuId', menuId);
    }
    if (includeChildren !== undefined) {
      params = params.append('IncludeChildren', includeChildren);
    }
    return this.http.get(url, { params, responseType: 'blob' });
  }
}
